// A response enum is its named list or any other string (ADR-0511, built by #2166): the
// contract writes each one `anyOf` the named closed list and `{type: string}`, so the generated
// type of the property is `string`, and a value a newer release added arrives as it was sent. A
// page keys its words on the named list, `components["schemas"]["<List>"]`, so the typecheck
// still fails for a listed value without a word, and reads a value through `listed`.
//
// Restates ADR-0322 and ADR-0511 decision 5a — a value outside the list is shown as received and
// marked unrecognized: never as one of the listed values, never as `unknown` (ADR-0074, ADR-0093)
// and never blank.

/** Whether `value` is one of `map`'s own keys, which are the named list the map is keyed on. Own
 * keys only, so `constructor` is not a value; the `call` form, as `pushOf`'s, so it does not
 * depend on a built-in an older browser lacks. */
export function listed<K extends string>(map: Readonly<Partial<Record<K, unknown>>>, value: string | null | undefined): value is K {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(map, value);
}

/** Whether `value` is one of `list`'s values, for a page that holds the named list as an array
 * (`web/src/api/enums.gen.ts`) rather than as a map. */
export function among<K extends string>(list: readonly K[], value: string | null | undefined): value is K {
  return typeof value === "string" && (list as readonly string[]).includes(value);
}

/** The word a value outside the list is drawn with: the value as received, marked. */
export function unrecognized(value: string | null | undefined): string {
  return value ? `unrecognized: ${value}` : "unrecognized";
}

/** `map`'s word for `value`, or the unrecognized mark. */
export function wordFor<K extends string>(map: Readonly<Record<K, string>>, value: string): string {
  return listed(map, value) ? map[value] : unrecognized(value);
}

/** A device's category in words: an access point, a switch, or the value as received and marked,
 * never the other one (ADR-0511 5a). `cap` capitalizes the first letter. */
export function categoryWord(category: string, cap = false): string {
  const w = category === "switch" ? "switch" : category === "ap" ? "access point" : unrecognized(category);
  return cap ? w.charAt(0).toUpperCase() + w.slice(1) : w;
}

/** A value read from a response and sent back in a request as it was received, where a request's
 * list is closed (ADR-0511 4a). This page's list may be older than the server's, so a value it does
 * not know is passed through for the server to judge: never dropped, which on a whole-set write
 * would remove it, and never replaced by a listed one. */
export function echoed<T extends string | number>(value: string | number): T {
  return value as T;
}
